import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref } from 'vue';
import { faCaretDown, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import CheckboxInput from '@/ui-components/CheckboxInput.vue';
import BasicButton from '@/ui-components/BasicButton.vue';
import BaseModal from '@/components/modals/vfm/BaseModal.vue';
import OfferReporterSuccessModal from '@/components/modals/offer-reporter/OfferReporterSuccessModal.vue';

import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery, useMutation } from '@/helpers/composables/useApollo';
import { useConstantStore, useLanguageStore } from '@/helpers/composables/useStores';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { VFMHelper } from '@/helpers/vfm/vfm-helper';
import { PopoverHelper } from '@/helpers/vfm/popover-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import Provider from '@/interfaces/provider';

import {
	Episode,
	Movie,
	MovieContent,
	ObjectType,
	Offer,
	Platform,
	Season,
	SeasonContent,
	Show,
	ShowContent,
	UpcomingRelease,
} from '@/@types/graphql-types';
import {
	ReportFeedbackDocument,
	ReportFeedbackMutation,
	ReportFeedbackMutationVariables,
} from '@/graphql/mutation/ReportFeedback.mutation';
import { SnowplowContext } from '@/helpers/tracking/providers';
import {
	GetSportProvidersByShortNamesDocument,
	GetSportProvidersByShortNamesQuery,
	GetSportProvidersByShortNamesQueryVariables,
} from '@/features/sports/graphql/queries/GetSportProvidersByShortNames.query';
import {
	GetTitleProvidersByShortNamesDocument,
	GetTitleProvidersByShortNamesQuery,
	GetTitleProvidersByShortNamesQueryVariables,
} from '@/pages/graphql/queries/GetTitleProvidersByShortNames.query';
import { APP_PLATFORM, APP_VERSION } from '@/stores/app.store';

export type OfferReporterModalTitle = Pick<Movie | Show | Season | Episode, 'objectId' | 'objectType'> &
	Pick<MovieContent | ShowContent | SeasonContent, 'scoring'>;


export default /*#__PURE__*/_defineComponent({
  __name: 'OfferReporterModal',
  props: {
    titleId: null,
    titleObjectId: null,
    titleObjectType: null,
    titleName: null,
    originalReleaseYear: null,
    titleOffers: { default: () => [] },
    additionalContexts: { default: () => [] }
  },
  setup(__props: any) {

const props = __props as { titleId: string, titleObjectId: number, titleObjectType:
			| ObjectType.Movie
			| ObjectType.ShowSeason
			| ObjectType.Show
			| ObjectType.ShowEpisode
			| ObjectType.SportEvent, titleName: string, originalReleaseYear?: number, titleOffers: Offer[] | UpcomingRelease[], additionalContexts: SnowplowContext[] };

const issueName = {
	mismatch: 'MISMATCH',
	title_not_available: 'TITLE_NOT_AVAILABLE',
	missing_offer: 'MISSING_OFFER',
	wrong_price: 'WRONG_OFFER',
	wrong_info: 'WRONG_INFO',
	bug: 'BUG',
	other: 'OTHER',
};



const { t, tc } = useI18n();
const { providers } = useConstantStore();
const { language, country, webLocale } = useLanguageStore();
const modal = ref<InstanceType<typeof BaseModal> | null>(null);

const itemData = reactive({
	commentInput: '',
	emailInput: '',
	errorNotification: false,
	externalLinkInput: '',
	isSending: false,
	issueSelected: '',
	providerSelected: '',
	providerSelectedClear: '',
	stayUpdated: false,
});

const queryVariables = computed<GetSportProvidersByShortNamesQueryVariables>(() => ({
	country: country.value,
	platform: Platform.Web,
}));

const sportProvidersByShortNamesQueryOptions = computed(() => ({
	enabled: props.titleObjectType === ObjectType.SportEvent,
}));
const { result: sportProvidersByShortNamesData } = useQuery<
	GetSportProvidersByShortNamesQuery,
	GetSportProvidersByShortNamesQueryVariables
>(GetSportProvidersByShortNamesDocument, queryVariables, sportProvidersByShortNamesQueryOptions);
const sportProvidersByShortName = computed(() =>
	(sportProvidersByShortNamesData.value?.packages || []).map(({ shortName }) => shortName)
);

const titleProvidersByShortNamesQueryOptions = computed(() => ({
	enabled: props.titleObjectType !== ObjectType.SportEvent,
}));
const { result: titleProvidersByShortNamesData } = useQuery<
	GetTitleProvidersByShortNamesQuery,
	GetTitleProvidersByShortNamesQueryVariables
>(GetTitleProvidersByShortNamesDocument, queryVariables, titleProvidersByShortNamesQueryOptions);
const titleProvidersByShortName = computed(() =>
	(titleProvidersByShortNamesData.value?.packages || []).map(({ shortName }) => shortName)
);

const { mutate } = useMutation<ReportFeedbackMutation, ReportFeedbackMutationVariables>(ReportFeedbackDocument);

const titleObjectTypeTranslation = computed(() => {
	const objectTypeTranslations = {
		[ObjectType.Movie]: tc('WEBAPP_MOVIE'),
		[ObjectType.Show]: tc('WEBAPP_SHOW'),
		[ObjectType.ShowSeason]: tc('WEBAPP_SEASON'),
		[ObjectType.ShowEpisode]: tc('WEBAPP_EPISODE'),
		[ObjectType.SportEvent]: tc('WEBAPP_EVENT'),
	};

	return objectTypeTranslations[props.titleObjectType].toLowerCase();
});

const issues = computed(() => {
	const object_type = titleObjectTypeTranslation.value;
	const object_type_capitalized = capitalize(titleObjectTypeTranslation.value);

	// the keys are values expected from the backend
	// translation keys need to be present in whole for automatic cleaning
	return [
		{
			technical_name: 'mismatch',
			translatable: t('WEBAPP_REPORT_ISSUE_MISMATCH', { object_type, object_type_capitalized }),
		},
		{
			technical_name: 'title_not_available',
			translatable: t('WEBAPP_REPORT_ISSUE_MISSING_TITLE_NOT_AVAILABLE', {
				object_type,
				object_type_capitalized,
			}),
		},
		{
			technical_name: 'missing_offer',
			translatable: t('WEBAPP_REPORT_ISSUE_MISSING_OFFER', { object_type, object_type_capitalized }),
		},
		{
			technical_name: 'wrong_price',
			translatable: t('WEBAPP_REPORT_ISSUE_WRONG_PRICE', { object_type, object_type_capitalized }),
		},
		{
			technical_name: 'wrong_info',
			translatable: t('WEBAPP_REPORT_ISSUE_METADATA', { object_type, object_type_capitalized }),
		},
		{
			technical_name: 'bug',
			translatable: t('WEBAPP_REPORT_ISSUE_BUG', { object_type, object_type_capitalized }),
		},
		{
			technical_name: 'cant_watch',
			translatable: t('WEBAPP_REPORT_ISSUE_CANT_WATCH', { object_type, object_type_capitalized }),
		},
		{
			technical_name: 'other',
			translatable: t('WEBAPP_REPORT_ISSUE_OTHER', { object_type, object_type_capitalized }),
		},
	];
});

const mandatoryComment = computed(() => ['wrong_info', 'bug', 'other'].includes(itemData.issueSelected));

const freetextPlaceholder = computed(() =>
	mandatoryComment.value ? tc('WEBAPP_REPORT_REQUIRED_FREETEXTPLACEHOLDER') : tc('WEBAPP_REPORT_FREETEXTPLACEHOLDER')
);

const translatedCountry = computed(() => tc(`WEBAPP_COUNTRY_${webLocale.value.toUpperCase()}`));

const isFormInvalid = computed<boolean>(() => {
	const { issueSelected, stayUpdated, emailInput, commentInput, providerSelected, externalLinkInput } = itemData;
	if (issueSelected === 'missing_offer') {
		return externalLinkInput !== '' ? false : true;
	}

	if (mandatoryComment.value) {
		if (issueSelected && stayUpdated) {
			return emailInput !== '' && commentInput !== '' ? false : true;
		} else {
			return commentInput !== '' ? false : true;
		}
	} else {
		if (issueSelected && stayUpdated) {
			return emailInput !== '' && providerSelected !== '' ? false : true;
		} else {
			return providerSelected ? false : true;
		}
	}
});

const titleProviders = computed(() => {
	const items = (props.titleOffers ?? []) as (Offer | UpcomingRelease)[];
	const titleProviderIDs = items.map(offer => offer.package?.packageId).filter(Boolean);
	return providers.value().filter((provider: any) => titleProviderIDs.indexOf(provider.packageId) !== -1);
});

const filteredProviders = computed(() => {
	const missingProvider = {
		clearName: `(${tc('WEBAPP_REPORT_MISSINGPROVIDER')})`,
		icon: '',
		monetizationTypes: [],
		packageId: 0,
		shortName: '',
		slug: '',
		technicalName: 'missing',
	} as Provider;
	const sportProviders = providers
		.value()
		.filter((provider: Provider) => sportProvidersByShortName.value.includes(provider.shortName));
	const titleProvidersData = providers
		.value()
		.filter((provider: Provider) => titleProvidersByShortName.value.includes(provider.shortName));
	const filtered = props.titleObjectType === ObjectType.SportEvent ? sportProviders : titleProvidersData;
	const providersData =
		itemData.issueSelected === 'missing_offer' ? filtered.slice().sort() : [...titleProviders.value].sort();
	return [missingProvider].concat(providersData);
});

const selectedIssueTranslatable = computed(
	() => issues.value.find(el => el.technical_name === itemData.issueSelected)?.translatable || '...'
);

async function openPopover(event: Event) {
	const selectedIssue = await PopoverHelper.create({
		popoverProps: {
			cssClass: 'issues-popover',
			boundingElement: event.currentTarget,
			position: 'left',
		},
		component: () => import('./IssuesListPopover.vue'),
		componentProps: {
			issues: issues.value,
			selectedIssue: itemData.issueSelected,
		},
	});

	if (!!selectedIssue) {
		handleIssueChange({ target: { value: selectedIssue } });
	}
}

function handleIssueChange(event?: any) {
	Object.assign(itemData, {
		commentInput: '',
		emailInput: '',
		errorNotification: false,
		externalLinkInput: '',
		isSending: false,
		providerSelected: '',
		providerSelectedClear: '',
		stayUpdated: false,
		issueSelected: event?.target?.value ?? '',
	});
}

async function openProviderPopover(event: Event) {
	if (!modal.value) return;

	const selectedProvider = await PopoverHelper.create({
		popoverProps: {
			cssClass: 'providers-popover',
			boundingElement: event.currentTarget,
			position: 'left',
			bottomGutter: -32,
		},
		component: () => import('./ProviderListPopover.vue'),
		componentProps: {
			providers: filteredProviders.value,
			selectedProvider: itemData.providerSelectedClear,
		},
	});

	if (!!selectedProvider) {
		handleProviderChange({ target: { value: selectedProvider } });
	}
}

function handleProviderChange(event: any) {
	const { technicalName, clearName } = event?.target?.value;

	itemData.providerSelected = technicalName ?? '';
	itemData.providerSelectedClear = clearName ?? '';
}

async function reportIssue() {
	if (itemData.isSending) return;

	itemData.isSending = true;

	const provider = providers.value().find((provider: Provider) => {
		return provider.technicalName === itemData.providerSelected;
	});

	try {
		if (itemData.emailInput) {
			const emailValid = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/.test(itemData.emailInput);
			if (!emailValid) {
				itemData.errorNotification = true;
				itemData.isSending = false;
				return;
			}
		}

		await mutate({
			input: {
				id: props.titleId,
				// @ts-ignore
				type: issueName[itemData.issueSelected],
				country: country.value,
				language: language.value,
				package: provider?.shortName || '',
				comment: itemData.commentInput,
				externalUrl: itemData.externalLinkInput,
				email: itemData.emailInput,
				platformVersion: `${APP_VERSION}-${APP_PLATFORM}`,
			},
		});

		TrackingHelper.trackEvent('offer_reporter', {
			action: 'send_feedback',
			label: itemData.issueSelected ?? '',
		});

		// close current modal
		VFMHelper.close();
		// then open OfferReporterSuccessModal
		await ModalHelper.openModal(
			OfferReporterSuccessModal,
			{ email: itemData.emailInput },
			{},
			'',
			false,
			props.additionalContexts
		);
	} catch (error) {
		itemData.errorNotification = true;
	}

	itemData.isSending = false;
}

function capitalize(str: string) {
	return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
}

return { __sfc: true,issueName, props, t, tc, providers, language, country, webLocale, modal, itemData, queryVariables, sportProvidersByShortNamesQueryOptions, sportProvidersByShortNamesData, sportProvidersByShortName, titleProvidersByShortNamesQueryOptions, titleProvidersByShortNamesData, titleProvidersByShortName, mutate, titleObjectTypeTranslation, issues, mandatoryComment, freetextPlaceholder, translatedCountry, isFormInvalid, titleProviders, filteredProviders, selectedIssueTranslatable, openPopover, handleIssueChange, openProviderPopover, handleProviderChange, reportIssue, capitalize, faCaretDown, faInfoCircle, CheckboxInput, BasicButton, BaseModal }
}

})